/**
 * Edition Configuration
 *
 * 2차원 Feature Flag 시스템
 * - Edition (기능 범위): Modeler > Developer > Viewer
 * - Deployment (인프라): Cloud (서버) / Standalone (로컬)
 *
 * 사용법:
 *   import { hasFeature, isMenuVisible, getConfig } from 'configs/edition.config';
 *
 *   if (hasFeature(FEATURES.AI_CHAT)) { ... }
 *   if (isMenuVisible('DDL')) { ... }
 *   const { isCloud, isModeler } = getConfig();
 */

// ==================== 상수 정의 ====================

/** Edition 유형 (기능 범위) */
export const EDITIONS = {
  MODELER: 'modeler',     // 풀 기능
  DEVELOPER: 'developer', // 개발자용 (엑셀 제외, DDL 추출 포함)
  VIEWER: 'viewer'        // 조회 전용
};

/** Deployment 유형 (인프라) */
export const DEPLOYMENTS = {
  CLOUD: 'cloud',         // 서버 기반 (OAuth, 협업)
  STANDALONE: 'standalone' // 로컬 전용 (IndexedDB, 오프라인)
};

/** 기능 상수 */
export const FEATURES = {
  // 모델 메뉴
  MODEL_CREATE: 'model.create',
  MODEL_OPEN: 'model.open',
  MODEL_SAVE: 'model.save',
  MODEL_COPY: 'model.copy',
  MODEL_EXPORT: 'model.export',
  MODEL_SETTINGS: 'model.settings',

  // 엑셀 기능
  EXCEL_IMPORT: 'excel.import',
  EXCEL_EXPORT: 'excel.export',

  // 다이어그램 메뉴
  ENTITY_CREATE: 'entity.create',
  RELATIONSHIP_CREATE: 'relationship.create',
  MEMO_CREATE: 'memo.create',
  PHYSICAL_CONVERT: 'physical.convert',
  IMAGE_DOWNLOAD: 'image.download',
  PDF_EXPORT: 'pdf.export',

  // 커뮤니티 메뉴
  COMMUNITY_PUBLISH: 'community.publish',
  COMMUNITY_SEARCH: 'community.search',
  COMMUNITY_SHARE: 'community.share',
  SHARE_GROUP: 'share.group',

  // 추가 기능
  DDL_EXTRACT: 'ddl.extract',
  MODEL_ANALYSIS: 'model.analysis',
  AI_CHAT: 'ai.chat',
  REALTIME_COLLAB: 'realtime.collab',
};

// ==================== Edition별 기능 매핑 ====================

/** Edition별 사용 가능 기능 목록 */
export const EDITION_FEATURES = {
  [EDITIONS.MODELER]: [
    // 모델 메뉴 - 전체
    FEATURES.MODEL_CREATE,
    FEATURES.MODEL_OPEN,
    FEATURES.MODEL_SAVE,
    FEATURES.MODEL_COPY,
    FEATURES.MODEL_EXPORT,
    FEATURES.MODEL_SETTINGS,
    // 엑셀 - 전체
    FEATURES.EXCEL_IMPORT,
    FEATURES.EXCEL_EXPORT,
    // 다이어그램 - 전체
    FEATURES.ENTITY_CREATE,
    FEATURES.RELATIONSHIP_CREATE,
    FEATURES.MEMO_CREATE,
    FEATURES.PHYSICAL_CONVERT,
    FEATURES.IMAGE_DOWNLOAD,
    FEATURES.PDF_EXPORT,
    // 커뮤니티 - 전체
    FEATURES.COMMUNITY_PUBLISH,
    FEATURES.COMMUNITY_SEARCH,
    FEATURES.COMMUNITY_SHARE,
    FEATURES.SHARE_GROUP,
    // 추가 기능
    FEATURES.MODEL_ANALYSIS,
    FEATURES.AI_CHAT,
    FEATURES.REALTIME_COLLAB,
  ],
  [EDITIONS.DEVELOPER]: [
    // 모델 메뉴 - 엑셀 제외
    FEATURES.MODEL_CREATE,
    FEATURES.MODEL_OPEN,
    FEATURES.MODEL_SAVE,
    FEATURES.MODEL_COPY,
    FEATURES.MODEL_EXPORT,
    FEATURES.MODEL_SETTINGS,
    // 다이어그램 - 전체
    FEATURES.ENTITY_CREATE,
    FEATURES.RELATIONSHIP_CREATE,
    FEATURES.MEMO_CREATE,
    FEATURES.PHYSICAL_CONVERT,
    FEATURES.IMAGE_DOWNLOAD,
    FEATURES.PDF_EXPORT,
    // 커뮤니티 - 전체
    FEATURES.COMMUNITY_PUBLISH,
    FEATURES.COMMUNITY_SEARCH,
    FEATURES.COMMUNITY_SHARE,
    FEATURES.SHARE_GROUP,
    // 추가 기능 - DDL 추출
    FEATURES.DDL_EXTRACT,
    FEATURES.AI_CHAT,
    FEATURES.REALTIME_COLLAB,
  ],
  [EDITIONS.VIEWER]: [
    // 모델 메뉴 - 읽기만
    FEATURES.MODEL_OPEN,
    // 다이어그램 - 조회/출력만
    FEATURES.IMAGE_DOWNLOAD,
    FEATURES.PDF_EXPORT,
    // 커뮤니티 - 검색만
    FEATURES.COMMUNITY_SEARCH,
  ],
};

// ==================== Deployment별 인프라 설정 ====================

/** Deployment별 인프라 구성 */
export const DEPLOYMENT_CONFIG = {
  [DEPLOYMENTS.CLOUD]: {
    storage: 'server',           // 서버 DB 사용
    auth: 'oauth',               // OAuth 인증
    offline: false,              // 오프라인 미지원
    aiChat: true,                // AI 채팅 가능
    realtimeCollab: true,        // 실시간 협업 가능
    requiresServer: true,        // 서버 필수
    localFileOnly: false,        // 로컬 파일 전용 아님
  },
  [DEPLOYMENTS.STANDALONE]: {
    storage: 'indexeddb',        // IndexedDB 사용
    auth: 'none',                // 인증 없음
    offline: true,               // 오프라인 지원
    aiChat: false,               // AI 채팅 불가
    realtimeCollab: false,       // 협업 불가
    requiresServer: false,       // 서버 불필요
    localFileOnly: true,         // 로컬 파일 전용
  },
};

// ==================== 메뉴별 에디션 매핑 ====================

/** 메뉴 shortLabel별 허용 에디션 */
export const MENU_EDITIONS = {
  // 모델 메뉴
  'N': ['modeler', 'developer'],           // 신규
  'CO': ['modeler', 'developer'],          // 클라우드 열기 (standalone에서는 제외)
  'CS': ['modeler', 'developer'],           // 클라우드 저장
  'CP': ['modeler', 'developer'],           // 복제
  'O': ['modeler', 'developer', 'viewer'],  // 파일 가져오기
  'S': ['modeler', 'developer', 'viewer'],  // 파일 내보내기
  'IE': ['modeler', 'developer', 'viewer'], // 엑셀 가져오기
  'EE': ['modeler', 'developer', 'viewer'], // 엑셀 내보내기
  'DDL': ['modeler', 'developer'],          // DDL 추출
  'ID': ['modeler', 'developer'],           // DDL 가져오기
  'MA': ['modeler', 'viewer'],              // 모델분석 (서브메뉴)
  'CEA': ['modeler', 'viewer'],             // 핵심엔터티 파악
  'RI': ['modeler', 'viewer'],              // 관계 추정
  'EC': ['modeler', 'viewer'],              // 엔터티 분류
  'CK': ['modeler', 'viewer'],              // 후보키 찾기
  'ES': ['modeler', 'viewer'],              // 엔터티 요약
  'SI': ['modeler', 'viewer'],              // 표준조사 (서브메뉴)
  'SN': ['modeler', 'viewer'],              // 명명규칙 조사
  'SW': ['modeler', 'viewer'],              // 단어 후보 조사
  'ST': ['modeler', 'viewer'],              // 용어 후보 조사
  'SD': ['modeler', 'viewer'],              // 도메인 후보 조사
  'QA': ['modeler', 'viewer'],              // 품질분석 (서브메뉴)
  'QI': ['modeler', 'viewer'],              // 품질조사
  'QM': ['modeler'],                        // 모델반영
  'MR': ['modeler', 'viewer'],              // 모델정제 (서브메뉴)
  'ENR': ['modeler', 'viewer'],             // 엔터티명 정제
  'ANR': ['modeler', 'viewer'],             // 속성명 정제
  'RRA': ['modeler', 'viewer'],             // 정제결과 반영
  'CA': ['modeler', 'developer'],           // 모든 모델 닫기
  'MC': ['modeler', 'developer'],           // 모델 설정

  // 다이어그램 메뉴
  'E': ['modeler', 'developer', 'viewer'],  // 엔터티 추가
  'REL': ['modeler', 'developer', 'viewer'], // 관계선 (서브메뉴)
  'IR': ['modeler', 'developer', 'viewer'],  // 식별 관계선
  'NR': ['modeler', 'developer', 'viewer'],  // 비식별 관계선
  'UR': ['modeler', 'developer', 'viewer'],  // 미확정 관계선
  'LD': ['modeler', 'developer', 'viewer'], // 논리 ERD
  'PD': ['modeler', 'developer', 'viewer'], // 물리 ERD
  'MD': ['modeler', 'developer', 'viewer'], // 메모 추가
  'PC': ['modeler', 'developer', 'viewer'], // 물리 전환
  'IM': ['modeler', 'developer', 'viewer'], // 이미지 다운로드
  'PF': ['modeler', 'developer', 'viewer'], // PDF 내보내기
  'DT': ['modeler', 'developer'],           // 다이어그램 테스트 (ADMIN)

  // 커뮤니티 메뉴
  'OP': ['modeler', 'developer'],           // 모델 공개하기
  'ODM': ['modeler', 'developer', 'viewer'], // 공개모델 검색
  'SG': ['modeler', 'developer'],           // 공유 그룹 관리
  'GM': ['modeler', 'developer'],           // 모델 공유하기
  'GS': ['modeler', 'developer', 'viewer'], // 공유모델 검색
};

// ==================== 메뉴별 배포유형 매핑 ====================

/** 메뉴 shortLabel별 허용 배포 유형 (미등록 시 모든 deployment에서 표시) */
export const MENU_DEPLOYMENTS = {
  'CO': ['cloud'],   // 클라우드 열기 - cloud 전용
  'CS': ['cloud'],   // 클라우드 저장 - cloud 전용
  'CP': ['cloud'],   // 복제 - cloud 전용
  // 로그 보기 - standalone 전용. 클라우드는 서버에 로그가 남지만 설치형은 사용자 PC 의
  // 로그 파일이 유일한 단서다(도움말 › 로그 보기 → 리포트 복사).
  'HL': ['standalone'],
  // 오류 신고 - cloud 전용. 최근 오류·환경 정보를 묶어 QBase 수집 경로로 QPM 에 남긴다.
  // 설치형은 서버 세션이 없으므로 「로그 보기 › 리포트 복사」가 같은 역할을 한다.
  'HR': ['cloud'],
};

// ==================== 헬퍼 함수 ====================

/** 현재 에디션 가져오기 */
export const getEdition = () => process.env.REACT_APP_EDITION || EDITIONS.MODELER;

/** 현재 배포 유형 가져오기 */
export const getDeployment = () => process.env.REACT_APP_DEPLOYMENT || DEPLOYMENTS.CLOUD;

/** 통합 설정 객체 가져오기 */
export const getConfig = () => {
  const edition = getEdition();
  const deployment = getDeployment();

  return {
    edition,
    deployment,
    features: EDITION_FEATURES[edition] || [],
    infra: DEPLOYMENT_CONFIG[deployment] || DEPLOYMENT_CONFIG[DEPLOYMENTS.CLOUD],

    // 편의 속성
    isModeler: edition === EDITIONS.MODELER,
    isDeveloper: edition === EDITIONS.DEVELOPER,
    isViewer: edition === EDITIONS.VIEWER,
    isCloud: deployment === DEPLOYMENTS.CLOUD,
    isStandalone: deployment === DEPLOYMENTS.STANDALONE,
  };
};

/**
 * 특정 기능 사용 가능 여부 체크
 * @param {string} feature - FEATURES 상수 값
 * @returns {boolean}
 */
export const hasFeature = (feature) => {
  const config = getConfig();
  const hasEditionFeature = config.features.includes(feature);

  // AI 채팅, 실시간 협업은 Cloud에서만 가능
  if (feature === FEATURES.AI_CHAT || feature === FEATURES.REALTIME_COLLAB) {
    return hasEditionFeature && config.isCloud;
  }

  return hasEditionFeature;
};

/**
 * 메뉴 항목 표시 여부 체크
 * @param {string} shortLabel - 메뉴 shortLabel
 * @returns {boolean}
 */
export const isMenuVisible = (shortLabel) => {
  const edition = getEdition();
  const deployment = getDeployment();

  // Edition 기반 필터링
  const allowedEditions = MENU_EDITIONS[shortLabel];
  if (allowedEditions && !allowedEditions.includes(edition)) return false;

  // Deployment 기반 필터링
  const allowedDeployments = MENU_DEPLOYMENTS[shortLabel];
  if (allowedDeployments && !allowedDeployments.includes(deployment)) return false;

  return true;
};

// ==================== 표시 이름 ====================

/** 에디션 표시 이름 */
export const EDITION_NAMES = {
  [EDITIONS.MODELER]: 'Modeler',
  [EDITIONS.DEVELOPER]: 'Developer',
  [EDITIONS.VIEWER]: 'Viewer',
};

/** 배포 유형 표시 이름 */
export const DEPLOYMENT_NAMES = {
  [DEPLOYMENTS.CLOUD]: 'Cloud',
  [DEPLOYMENTS.STANDALONE]: 'Standalone',
};

/** 현재 버전 정보 문자열 (예: "Cloud-Modeler") */
export const getVersionString = () => {
  const config = getConfig();
  return `${DEPLOYMENT_NAMES[config.deployment]}-${EDITION_NAMES[config.edition]}`;
};
